import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import resolveOrderRef from "@/lib/resolveOrderRef";
import { useAuth } from "@/lib/AuthContext";

/**
 * Resolver page for short / external deep-links to the Label Print page.
 *
 * Mirrors OrderShortLink but redirects to `/labels/{uuid}` and preserves
 * any query string (e.g. ?autoprint=1) through the redirect.
 */
export default function LabelShortLink() {
  const { ref } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | syncing | not-found | error
  const canSync = user?.role === "admin" || user?.role === "manager";

  useEffect(() => {
    let cancelled = false;
    let syncIndicatorTimer = null;
    const tail = `${loc.search || ""}${loc.hash || ""}`;
    async function run() {
      syncIndicatorTimer = setTimeout(() => {
        if (!cancelled && canSync) setStatus("syncing");
      }, 400);
      try {
        const result = await resolveOrderRef(ref, { canSync });
        if (cancelled) return;
        if (result?.order?.id) {
          if (result.synced) toast.success("Found via Base44 sync");
          nav(`/labels/${result.order.id}${tail}`, { replace: true });
          return;
        }
        setStatus("not-found");
      } catch (err) {
        console.warn("LabelShortLink: resolve failed", err);
        if (!cancelled) setStatus("error");
      } finally {
        if (syncIndicatorTimer) clearTimeout(syncIndicatorTimer);
      }
    }
    run();
    return () => {
      cancelled = true;
      if (syncIndicatorTimer) clearTimeout(syncIndicatorTimer);
    };
  }, [ref, nav, loc.search, loc.hash, canSync]);

  if (status === "loading") {
    return (
      <div className="text-zinc-500" data-testid="label-short-link-loading">
        Looking up labels for <span className="font-mono font-semibold">{ref}</span>…
      </div>
    );
  }

  if (status === "syncing") {
    return (
      <div className="text-zinc-500" data-testid="label-short-link-syncing">
        Syncing from Base44 to find <span className="font-mono font-semibold">{ref}</span>…
        <div className="text-xs text-zinc-400 mt-1">This can take a few seconds.</div>
      </div>
    );
  }

  if (status === "not-found") {
    return (
      <div className="space-y-3" data-testid="label-short-link-not-found">
        <h1 className="font-heading font-black text-2xl">Order not found</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          No order matches <span className="font-mono font-semibold">{ref}</span>{canSync ? " — even after re-syncing from Base44" : ""}.
          {!canSync && " It may not be synced from Base44 yet — ask an admin to run a sync."}
        </p>
        <Link to="/orders" className="text-[#C8102E] font-semibold hover:underline">
          Go to orders →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="label-short-link-error">
      <h1 className="font-heading font-black text-2xl">Couldn&apos;t open labels</h1>
      <p className="text-sm text-zinc-600 dark:text-zinc-300">
        Something went wrong while looking up <span className="font-mono font-semibold">{ref}</span>.
        Please try again, or open it from the Orders page.
      </p>
      <Link to="/orders" className="text-[#C8102E] font-semibold hover:underline">
        Go to orders →
      </Link>
    </div>
  );
}
